import { getMessagingInstance, VAPID_KEY } from "./firebaseConfig";
import { getToken, onMessage, deleteToken, isSupported } from "firebase/messaging";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import { isPushCapableBrowser } from "@/lib/browserDetection";
import { ServiceWorkerManager } from "@/lib/serviceWorkerManager";

/**
 * FCM Service for handling Firebase Cloud Messaging operations
 */
export class FCMService {
  /**
   * Request notification permission and generate FCM token
   * @returns Promise<string | null> - FCM token or null if permission denied
   */
  static async requestPermissionAndGetToken(): Promise<string | null> {
    try {
      // Covers Instagram/Facebook in-app browsers, insecure contexts, and any
      // browser without serviceWorker / Notification / PushManager.
      if (!isPushCapableBrowser()) {
        console.warn("FCM: Skipping initialization - browser cannot support push");
        return null;
      }

      // Check if the browser supports notifications
      if (!("Notification" in window)) {
        console.warn("This browser does not support notifications");
        return null;
      }

      // Check if service worker should be registered (includes all necessary checks)
      if (!ServiceWorkerManager.shouldRegister()) {
        console.warn("Service worker registration not suitable for this browser");
        return null;
      }

      // Ensure service worker is registered before proceeding
      const registration = await ServiceWorkerManager.register('/firebase-messaging-sw.js', {
        skipInAppBrowsers: true,
        skipProblematicBrowsers: true,
      });

      if (!registration) {
        console.warn("Failed to register service worker for FCM");
        return null;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== "granted") {
        console.warn("Notification permission denied");
        return null;
      }

      // Get FCM token using VAPID key from config
      const messagingInstance = getMessagingInstance();
      if (!messagingInstance) {
        console.warn("Messaging not available on server side");
        return null;
      }
      
      try {
        const token = await getToken(messagingInstance, {
          vapidKey: VAPID_KEY,
          // Reuse the worker we registered above. Without this the SDK falls back
          // to registerDefaultSw(), which registers a second, config-less worker.
          serviceWorkerRegistration: registration,
        });
        
        if (token) {
          console.log("FCM Token generated:", token);
          return token;
        } else {
          console.warn("No registration token available");
          return null;
        }
      } catch (tokenError: any) {
        console.error("Error getting FCM token:", tokenError);
        // If it's a service worker registration error, log it but don't break the app
        if (tokenError.code === 'messaging/failed-service-worker-registration') {
          console.warn("Service worker registration failed, FCM will not be available");
        }
        return null;
      }
    } catch (error) {
      console.error("Error getting FCM token:", error);
      return null;
    }
  }

  /**
   * Register FCM token with the backend
   * @param token - FCM token to register
   * @returns Promise<boolean> - Success status
   */
  static async registerTokenWithBackend(token: string): Promise<boolean> {
    try {
      const response = await ehjzleeInstance.post("/notifications/save-token", {
        token,
      });
      
      console.log("FCM token registered successfully:", response.data);
      return true;
    } catch (error) {
      console.error("Error registering FCM token:", error);
      return false;
    }
  }

  /**
   * Delete FCM token and cleanup on logout
   * @returns Promise<boolean> - Success status
   */
  static async deleteTokenAndCleanup(): Promise<boolean> {
    try {
      // Delete the FCM token
      const messagingInstance = getMessagingInstance();
      if (!messagingInstance) {
        console.warn("Messaging not available on server side");
        return false;
      }
      
      // deleteToken() takes no options and calls the SDK's registerDefaultSw()
      // whenever messaging.swRegistration is unset — which it is on any page load
      // that didn't itself call getToken(). That would register a second, bare
      // /firebase-messaging-sw.js at scope /firebase-cloud-messaging-push-scope
      // with no config query params, so it would fail to initialize Firebase.
      // Bail out when nothing is registered, and otherwise hand the SDK our own
      // registration — the same plain assignment the SDK's updateSwReg() does.
      const existingRegistration = await ServiceWorkerManager.getRegistration();
      if (!existingRegistration) {
        console.warn("No service worker registered, skipping token deletion");
        return true;
      }
      (messagingInstance as unknown as { swRegistration?: ServiceWorkerRegistration })
        .swRegistration = existingRegistration;
      
      try {
        await deleteToken(messagingInstance);
      } catch (deleteError: any) {
        // If it's a service worker registration error, just log it and continue
        if (deleteError.code === 'messaging/failed-service-worker-registration') {
          console.warn("Service worker registration failed during cleanup, this is normal");
          return true;
        }
        // Re-throw other errors
        throw deleteError;
      }
      console.log("FCM token deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting FCM token:", error);
      // Don't throw the error, just return false to indicate failure
      return false;
    }
  }

  /**
   * Set up foreground message handler
   * @param onMessageReceived - Callback function to handle received messages
   */
  static setupForegroundHandler(
    onMessageReceived: (payload: any) => void
  ): () => void {
    const messagingInstance = getMessagingInstance();
    if (!messagingInstance) {
      console.warn("Messaging not available on server side");
      return () => {}; // Return empty cleanup function
    }
    
    const unsubscribe = onMessage(messagingInstance, (payload: any) => {
      console.log("Message received in foreground:", payload);
      onMessageReceived(payload);
    });

    return unsubscribe;
  }

  /**
   * Initialize FCM for the application
   * This should be called after user login
   * @param onMessageReceived - Optional callback for foreground messages
   * @returns Promise<boolean> - Success status
   */
  static async initializeFCM(
    onMessageReceived?: (payload: any) => void
  ): Promise<boolean> {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        console.warn("FCM initialization skipped - not in browser environment");
        return false;
      }

      // isSupported() probes IndexedDB openability plus the SW/Push/Notification
      // globals. It's authoritative but async, which is why getMessagingInstance()
      // keeps its own cheap synchronous guard for the module-scope crash class.
      let supported = false;
      try {
        supported = await isSupported();
      } catch {
        supported = false;
      }
      if (!supported) {
        console.warn("FCM initialization skipped - messaging not supported in this browser");
        return false;
      }

      if (!isPushCapableBrowser()) {
        console.warn("FCM initialization skipped - browser cannot support push");
        return false;
      }

      // Check if service worker should be registered (includes all necessary checks)
      if (!ServiceWorkerManager.shouldRegister()) {
        console.warn("FCM initialization skipped - service worker not suitable");
        return false;
      }

      // Ensure service worker is registered before initializing FCM
      const registration = await ServiceWorkerManager.register('/firebase-messaging-sw.js', {
        skipInAppBrowsers: true,
        skipProblematicBrowsers: true,
      });

      if (!registration) {
        console.warn("FCM initialization skipped - service worker registration failed");
        return false;
      }

      // Get FCM token
      const token = await this.requestPermissionAndGetToken();
      
      if (!token) {
        return false;
      }

      // Register token with backend
      const registered = await this.registerTokenWithBackend(token);
      
      if (!registered) {
        return false;
      }

      // Set up foreground message handler if callback provided
      if (onMessageReceived) {
        this.setupForegroundHandler(onMessageReceived);
      }

      return true;
    } catch (error) {
      console.error("Error initializing FCM:", error);
      return false;
    }
  }

  /**
   * Handle token refresh
   * This should be called when the token is refreshed
   * @param newToken - New FCM token
   */
  static async handleTokenRefresh(newToken: string): Promise<void> {
    try {
      console.log("FCM token refreshed:", newToken);
      
      // Register new token with backend
      await this.registerTokenWithBackend(newToken);
    } catch (error) {
      console.error("Error handling token refresh:", error);
    }
  }
}
